import { BV_TRACKING_DEBUG_KEY } from '../config/debug';
import type { TrackingBusEventName } from '../constants/trackingBusEvent';
import { TrackingBusEvent } from '../constants/trackingBusEvent';

/**
 * Client-side debug logging only when `bv_tracking_debug` is set in localStorage (non-empty value).
 * Enable e.g. `localStorage.setItem('bv_tracking_debug', '1');` then reload. Disable: `removeItem`.
 * Do not log signed collect URLs, initialize secrets, or raw POST bodies.
 */
export class TrackingDebugService {
  isEnabled(): boolean {
    try {
      if (typeof localStorage === 'undefined') {
        return false;
      }
      const v = localStorage.getItem(BV_TRACKING_DEBUG_KEY);

      return v != null && v !== '';
    } catch {
      return false;
    }
  }

  log(
    scope: string,
    message: string,
    details?: Record<string, unknown>
  ): void {
    if (!this.isEnabled()) {
      return;
    }
    const ts = new Date().toISOString();
    /** ms since navigation start — finer ordering when multiple logs share the same ISO ms */
    const elapsedMs = typeof performance !== 'undefined' && typeof performance.now === 'function' ? Math.round(performance.now()) : undefined;
    console.debug(elapsedMs != null ? `[BirdsviewTracking:${scope}][${ts}][+${elapsedMs}ms]` : `[BirdsviewTracking:${scope}][${ts}]`, message, details ?? '');
  }

  /**
   * Redacted summaries for EventBus debug lines — raw payloads can contain ids or customer hints.
   * `event` allows arbitrary `string` so logs stay usable if a legacy or dynamic event slips through
   * at runtime; the `(string & {})` trick preserves literal autocomplete for `TrackingBusEventName`.
   */
  summarizeBusPayload(
    event: TrackingBusEventName | (string & {}),
    payload: unknown
  ): Record<string, unknown> {
    if (event === TrackingBusEvent.PageBfcache) {
      return { restored: true };
    }
    if (event === TrackingBusEvent.PageUnload && typeof payload === 'object' && payload !== null && 'persisted' in payload) {
      return { persisted: (payload as { persisted?: boolean }).persisted === true };
    }
    if (payload == null) {
      return {};
    }
    if (event === TrackingBusEvent.TabReset && typeof payload === 'object' && payload !== null && 'tabId' in payload) {
      const tabId = (payload as { tabId?: string }).tabId;

      return { tabIdPresent: typeof tabId === 'string' && tabId.length > 0 };
    }
    if (event === TrackingBusEvent.CustomerResolved && typeof payload === 'object' && payload !== null && 'customerId' in payload) {
      const customerId = (payload as { customerId?: string | null }).customerId;

      return { customerIdPresent: customerId != null && customerId !== '' };
    }

    return { type: typeof payload };
  }
}
